import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiSearch,
  FiFilter,
  FiCalendar,
  FiClock,
  FiTrendingUp,
  FiAward,
  FiBookOpen,
  FiEye,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiList,
  FiTarget,
  FiPercent,
  FiActivity,
  FiZap
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';

const badgeClass = (v) => ({ success: 'bg-emerald-100 text-emerald-800', warning: 'bg-amber-100 text-amber-800', danger: 'bg-red-100 text-red-800', info: 'bg-slate-100 text-slate-700' }[v] || 'bg-slate-100 text-slate-700');

const UserTestResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    subject: '',
    examType: '',
    status: '',
    page: 1,
    limit: 20
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  const { user } = useAuth();
  const { showError } = useNotification();

  const loadResults = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 [FRONTEND DEBUG] Starting to load user test results');
      console.log('🔄 [FRONTEND DEBUG] Current user:', user);
      console.log('🔄 [FRONTEND DEBUG] Auth token exists:', !!localStorage.getItem('token'));
      console.log('🔄 [FRONTEND DEBUG] Request filters:', { filters, sortBy, sortOrder });

      const requestParams = {
        ...filters,
        sortBy,
        sortOrder
      };
      console.log('🔄 [FRONTEND DEBUG] Final request params:', requestParams);

      console.log('🌐 [FRONTEND DEBUG] Making API call to getUserTestResults...');
      const response = await testAPI.getUserTestResults(requestParams);

      console.log('📊 [FRONTEND DEBUG] Raw API Response:', response);
      console.log('📊 [FRONTEND DEBUG] Response status:', response.status);
      console.log('📊 [FRONTEND DEBUG] Response data structure:', {
        hasData: !!response.data,
        hasDataData: !!response.data?.data,
        hasResults: !!response.data?.data?.results,
        hasPagination: !!response.data?.data?.pagination
      });

      const resultsData = response.data?.data?.results || [];
      const paginationData = response.data?.data?.pagination || {};
      
      console.log('✅ [FRONTEND DEBUG] Processed results:', {
        resultsCount: resultsData.length,
        resultsArray: resultsData,
        pagination: paginationData
      });
      
      setResults(resultsData);
      setPagination(paginationData);
    } catch (err) {
      console.error('💥 [FRONTEND DEBUG] Failed to load test results:', err);
      console.error('💥 [FRONTEND DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        responseData: err.response?.data,
        responseStatus: err.response?.status,
        stack: err.stack
      });
      
      const data = err.response?.data;
      const errorMessage = data?.error || data?.message || err.message || 'Failed to load test results. Please try again.';
      setError(errorMessage);
      showError(`Failed to load test results: ${errorMessage}`);
      setResults([]);
    } finally {
      setLoading(false);
      console.log('🏁 [FRONTEND DEBUG] loadResults completed');
    }
  }, [filters, sortBy, sortOrder, user, showError]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setFilters({
      subject: '',
      examType: '',
      status: '',
      page: 1,
      limit: 20
    });
    setSearchTerm('');
    setSortBy('completedAt');
    setSortOrder('desc');
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const calculateStats = () => {
    if (results.length === 0) return { totalTests: 0, averageScore: 0, passedTests: 0, passRate: 0 };

    const totalTests = results.length;
    const totalScore = results.reduce((sum, result) => sum + (result.score?.percentage || 0), 0);
    const averageScore = Math.round(totalScore / totalTests);
    const passedTests = results.filter(result => (result.score?.percentage || 0) >= 50).length;
    const passRate = Math.round((passedTests / totalTests) * 100);

    return { totalTests, averageScore, passedTests, passRate };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading test results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Test results</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track performance and progress across all tests</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/comprehensive-insights" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">
              <FiZap size={16} /> Expert Intelligence
            </Link>
            <button type="button" onClick={loadResults} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              <FiRefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiTarget size={20} /></div>
              <div><div className="text-lg font-semibold text-slate-900">{stats.totalTests}</div><div className="text-xs text-slate-500">Total tests</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiPercent size={20} /></div>
              <div><div className="text-lg font-semibold text-slate-900">{stats.averageScore}%</div><div className="text-xs text-slate-500">Average</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiAward size={20} /></div>
              <div><div className="text-lg font-semibold text-slate-900">{stats.passedTests}</div><div className="text-xs text-slate-500">Passed</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><FiTrendingUp size={20} /></div>
              <div><div className="text-lg font-semibold text-slate-900">{stats.passRate}%</div><div className="text-xs text-slate-500">Pass rate</div></div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" placeholder="Search results..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-200 focus:border-slate-300" />
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${showFilters ? 'bg-slate-100 border-slate-300 text-slate-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
                <FiFilter size={16} /> Filters
              </button>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button type="button" onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} title="Grid"><FiGrid size={18} /></button>
                <button type="button" onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} title="List"><FiList size={18} /></button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Subject</label>
                <select value={filters.subject} onChange={(e) => handleFilterChange('subject', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                  <option value="">All</option>
                  <option value="english">English</option>
                  <option value="mathematics">Mathematics</option>
                  <option value="physics">Physics</option>
                  <option value="chemistry">Chemistry</option>
                  <option value="biology">Biology</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Exam type</label>
                <select value={filters.examType} onChange={(e) => handleFilterChange('examType', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                  <option value="">All</option>
                  <option value="waec">WAEC</option>
                  <option value="jamb">JAMB</option>
                  <option value="post-utme">Post-UTME</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                  <option value="">All</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sort</label>
                <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
                  <option value="completedAt">Date</option>
                  <option value="score.percentage">Score</option>
                  <option value="session.timeTaken">Time</option>
                </select>
              </div>
              <div className="flex items-end"><button type="button" onClick={clearFilters} className="text-sm font-medium text-slate-600 hover:text-slate-900">Clear</button></div>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6 flex items-center justify-between">
            <p className="text-sm text-red-800">{error}</p>
            <button type="button" onClick={loadResults} className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200">Try again</button>
          </div>
        )}

        {results.length === 0 && !loading && !error && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-12 text-center">
            <FiActivity size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No results yet</h3>
            <p className="text-sm text-slate-500 mb-4">Complete tests to see results here.</p>
            <Link to="/tests" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">Browse tests</Link>
          </div>
        )}

        {results.length > 0 && (
          <>
            <div className={viewMode === 'list' ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'}>
              {results.map((result) => {
                const isRegularTest = result.test && result.test._id;
                const isPracticeTest = result.testType === 'practice';
                const testTitle = isRegularTest ? result.test.title : `${result.examType?.toUpperCase()} ${result.subject} Practice Test`;
                const subject = isRegularTest ? result.test.subject : result.subject;
                const scoreColor = getScoreColor(result.score?.percentage || 0);
                const scorePct = result.score?.percentage || 0;
                return (
                  <div key={result._id} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex flex-col">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="text-sm font-medium text-slate-900 flex-1 min-w-0">
                        {isRegularTest ? (
                          <Link to={`/tests/${result.test._id}/results`} state={{ submissionId: result._id }} className="hover:underline truncate block">{testTitle}</Link>
                        ) : isPracticeTest ? (
                          <Link to={`/practice-tests/${result._id}/results`} className="hover:underline truncate block">{testTitle}</Link>
                        ) : (
                          <span className="truncate block">{testTitle}</span>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {isPracticeTest && <span className={badgeClass('info') + ' text-xs px-2 py-0.5 rounded'}>Practice</span>}
                          {(result.aiInsights?.detailedInsight || result.aiInsights?.readinessLevel || (result.aiInsights?.strengths?.length || result.aiInsights?.weaknesses?.length)) && (
                            <span className={badgeClass('info') + ' text-xs px-2 py-0.5 rounded inline-flex items-center gap-1'} title="Expert Intelligence insight"><FiZap size={10} /> Insight</span>
                          )}
                        </div>
                      </h3>
                      <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${badgeClass(scoreColor)}`}>{getGrade(scorePct)}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
                      <span className="inline-flex items-center gap-1"><FiBookOpen size={12} />{subject}</span>
                      <span className="inline-flex items-center gap-1"><FiCalendar size={12} />{formatDate(result.session?.endTime || result.session?.completedAt || result.createdAt)}</span>
                      <span className="inline-flex items-center gap-1"><FiClock size={12} />{formatDuration(result.session?.timeTaken || 0)}</span>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-semibold ${scoreColor === 'success' ? 'bg-emerald-100 text-emerald-800' : scoreColor === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{scorePct}%</div>
                      <div className="text-xs text-slate-600">{result.analytics?.correctAnswers ?? 0}/{result.analytics?.totalQuestions ?? 0} correct · {result.score?.raw ?? 0} pts</div>
                    </div>
                    <div className="mt-auto">
                      {isRegularTest ? (
                        <Link to={`/tests/${result.test._id}/results`} state={{ submissionId: result._id }} className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                          <FiEye size={14} />{result.aiInsights?.detailedInsight || result.aiInsights?.readinessLevel ? 'Details & insight' : 'View details'}
                        </Link>
                      ) : isPracticeTest ? (
                        <Link to={`/practice-tests/${result._id}/results`} className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                          <FiEye size={14} /> View results
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-2 border border-slate-100 rounded-lg text-sm text-slate-400 cursor-not-allowed"><FiEye size={14} /> Practice result</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <button type="button" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={!pagination.hasPrevPage} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50">
                  <FiChevronLeft size={16} /> Previous
                </button>
                <span className="text-sm text-slate-600">Page {pagination.currentPage} of {pagination.totalPages}</span>
                <button type="button" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={!pagination.hasNextPage} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50">
                  Next <FiChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserTestResults;